import React, { useState, useRef, useEffect } from 'react';
import KickImage from 'Reuseables/KickImage';
import { IconContext } from 'react-icons';
import { GrFormDown } from 'react-icons/gr';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import {
  doFetchCurrencyRate,
  doFetchProfile,
  doUpdateUserProfile,
  updateUserCountry,
} from '../../../actions/authAction';
import {
  doFetchCountries,
  doFetchProvinces,
} from '../../../actions/postalCodeAction';
import { CURRENCY_CODE } from '../../../constants';
import { useOutsideAlerter } from '../../../helpers/dom';
import AuthInterface, {
  UserDataInterface,
} from '../../../interface/reducers/auth';
import DataPostalCodeInterface, {
  CountriesInterface,
  ProvinceInterface,
} from '../../../interface/reducers/postalCode';

// ========= action import section ========= //
// ====== end of action import section ====== //

// ========= interface import section ========= //
// ====== end of interface import section ====== //

type stateToProps = {
  dataPostalCode: DataPostalCodeInterface;
  dataAuth: AuthInterface;
};

interface SellerLocationInterface {
  // state to props
  countries: CountriesInterface[];
  provinces: ProvinceInterface[];
  userData: UserDataInterface;
  // dispatch to props
  getCountries: () => any;
  getProvinces: (country: string) => any;
  updateProfile: (params: any) => Promise<any>;
  doGetProfile: () => Promise<any>;
  doUpdateUserCountry: (params: any) => Promise<any>;
  fetchCurrencyRate: (payload) => any;
}

/**
 * Seller Location Tab on Setting User Dashboard section for Kick Avenue 3.0 Website.
 * ===============================================
 * @param {SellerLocationInterface} props for this component.
 * @return {JSX.Element}
 */
function SellerLocationTab({
  getCountries,
  getProvinces,
  countries,
  provinces,
  userData,
  doUpdateUserCountry,
  updateProfile,
  doGetProfile,
  fetchCurrencyRate,
}: SellerLocationInterface): JSX.Element {
  const wrapperRefCountry = useRef(null);
  const [isCountryOpened, setCountryOpened] = useState(false);
  useOutsideAlerter(wrapperRefCountry, () => setCountryOpened(false));
  const [countryInput, setCountryInput] = useState('');
  const [countryList, setCountryList] = useState([
    { code: '', name: 'Select Country' },
  ]);
  const [oldCountryList, setOldCountryList] = useState([
    { code: '', name: 'Select Country' },
  ]);
  const wrapperRefProvince = useRef(null);
  const [isProvinceOpened, setProvinceOpened] = useState(false);
  useOutsideAlerter(wrapperRefProvince, () => setProvinceOpened(false));

  const [firstRender, setFirstRender] = useState(false);
  const [disabledButton, setDisabledButton] = useState(false);
  const [country, setCountry] = useState<any>({});
  const [selectedCountry, setSelectedCountry] = useState('');
  const [province, setProvince] = useState({});
  const [selectedProvince, setSelectedProvince] = useState(0);

  useEffect(() => {
    getCountries();
  }, []);

  useEffect(() => {
    if (countries.length) {
      const countryListOptions = countries.map((item) => ({
        code: item.code3 ? item.code3 : item.code,
        name: item.name,
      }));
      setCountryList(countryListOptions)
      setOldCountryList(countryListOptions)

      let c = countries.find((t) => t.code3 === userData.country_code)
      setCountry(c);
      setSelectedCountry(userData.country_code);
      setCountryInput(c?.name || '');
      if (c?.code3 === 'IDN') {
        getProvinces(userData.country_code);
      }
    }
  }, [countries]);

  useEffect(() => {
    if (
      !firstRender &&
      !province.hasOwnProperty('id') &&
      provinces.length &&
      selectedCountry &&
      userData.user_setting !== null
    ) {
      setProvince(
        provinces.find(
          (t) => t.id === userData.user_setting.seller_location_id,
        ),
      );
      setSelectedProvince(userData.user_setting.seller_location_id);
      setFirstRender(true);
    }
  }, [provinces]);

  useEffect(() => {
    if (
      selectedCountry === '' ||
      (selectedProvince === 0 && selectedCountry === 'IDN') ||
      (selectedCountry !== 'IDN' && selectedCountry === userData.country_code) ||
      (selectedCountry === userData.country_code && userData.user_setting !== null &&
        selectedProvince === userData.user_setting.seller_location_id)
    ) {
      setDisabledButton(true);
    } else {
      setDisabledButton(false);
    }
  }, [country, province]);

  const onChangeCountryInput = (e) => {
    setCountryInput(e.target.value);
    const newCountryList = oldCountryList.filter((t) =>
      t.name.toLowerCase().includes(e.target.value.toLowerCase()),
    );
    setCountryList(newCountryList);
  };

  const onSubmit = async () => {
    const dataUser = {
      email: userData.email,
      first_name: userData.first_name,
      family_name: userData.family_name,
      birthed_at: userData.birthed_at,
      seller_location_id: selectedProvince,
    };
    const dataCountry = {
      country_code: selectedCountry,
    };
    if (!disabledButton) {

      try {
        if (selectedCountry === 'IDN') {
          const { value } = await updateProfile(dataUser)
          if (value.status_code === 200) {
            await doUpdateUserCountry(dataCountry)
          }
        } else {
          await doUpdateUserCountry(dataCountry)
        }

        try {
          const currencyCode = CURRENCY_CODE[selectedCountry === 'IDN' ? 'IDN' : 'ETC'];
          await fetchCurrencyRate(currencyCode)
        } catch (error) {
          await fetchCurrencyRate(CURRENCY_CODE['IDN'])
        }
        
        toast.success('Success updating your location.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          progress: undefined,
          style: {
            padding: '16px',
            backgroundColor: '#159953',
            margin: '16px',
            borderRadius: '10px',
            fontFamily: 'Nunito-Regular',
            fontSize: '14px',
          },
        });
        setDisabledButton(true);
        doGetProfile()
      } catch (e) {
        toast.error(
          e.response?.data?.error?.message
            ? e.response.data.error.message
            : 'Sorry, we have a problem!. Please inform our friendly staff via LINE @kickavenue',
          {
            position: 'top-right',
            autoClose: 5000,
            closeOnClick: true,
            pauseOnHover: true,
          },
        );
      }
    }
  };

  return (
    <div className={'bank-account-container'}>
      <div className={'form-parent'}>
        <p>Country</p>
        <div
          className={'text-box'}
          onClick={() => (countryList.length ? setCountryOpened(true) : null)}
        >
          <input
            placeholder={'Select Country'}
            value={countryInput}
            onChange={onChangeCountryInput}
          />
          {/* <p>{country && country.name ? country.name : 'Select Country'}</p> */}
          <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
            <div className={'icon-dropdown'}>
              <GrFormDown />
            </div>
          </IconContext.Provider>
        </div>
        {isCountryOpened && (
          <div
            ref={wrapperRefCountry}
            className={'search-product-result-container'}
          >
            {countryList.map((t) => {
              const onClick = () => {
                setCountry(t);
                setSelectedCountry(t.code);
                setCountryOpened(false);
                getProvinces(t.code);
                setCountryInput(t.name);
                setCountryList(oldCountryList);
                if (
                  t.code !== userData.country_code ||
                  t.code !== selectedCountry
                ) {
                  setSelectedProvince(0);
                  setProvince({});
                }
              };
              return (
                <div
                  className={`search-bank-item${
                    t.code === selectedCountry ? ' selected' : ''
                  }`}
                  onClick={onClick}
                  key={t.code}
                >
                  <p>{t.name}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {selectedCountry && selectedCountry === 'IDN' &&
        <div className={'form-parent'}>
          <p>Province</p>
          <div
            className={'text-box'}
            onClick={() => (provinces.length ? setProvinceOpened(true) : null)}
          >
            <p>{province && province.name ? province.name : 'Select Province'}</p>
            <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
              <div className={'icon-dropdown'}>
                <GrFormDown />
              </div>
            </IconContext.Provider>
          </div>
          {isProvinceOpened && (
            <div
              ref={wrapperRefProvince}
              className={'search-product-result-container'}
            >
              {provinces.map((t) => {
                const onClick = () => {
                  console.log(t);
                  setProvince(t);
                  setSelectedProvince(t.id);
                  setProvinceOpened(false);
                };
                return (
                  <div
                    className={`search-bank-item${
                      t === province ? ' selected' : ''
                    }`}
                    onClick={onClick}
                    key={t.id}
                  >
                    <p>{t.name}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      }
      <div
        className={`button-submit ${disabledButton ? 'disabled' : ''}`}
        onClick={onSubmit}
      >
        <p>Save Changes</p>
      </div>
    </div>
  );
}

const mapStateToProps = (state: stateToProps) => {
  return {
    countries: state.dataPostalCode.countries.data,
    provinces: state.dataPostalCode.provinces.data,
    userData: state.dataAuth.userdata,
  };
};

const mapDispatchToProps = (dispatch: (arg0: any) => any) => {
  return {
    getCountries: () => dispatch(doFetchCountries()),
    getProvinces: (country: string) => dispatch(doFetchProvinces(country)),
    updateProfile: (params: any) => dispatch(doUpdateUserProfile(params)),
    doUpdateUserCountry: (params: any) => dispatch(updateUserCountry(params)),
    doGetProfile: () => dispatch(doFetchProfile()),
    fetchCurrencyRate: (payload) => dispatch(doFetchCurrencyRate(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SellerLocationTab);
